import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";
import { slugify } from "@/lib/slugify";

export interface BrandRecord {
  id: ID;
  name: string;
  key: string;
  description: string;
  /** When true, listed under “Popular” in pickers (e.g. vehicle name builder). */
  isPopular: boolean;
  /** Data URL (image/*) or null when no logo */
  logoDataUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

const LS_KEY = "inventory.brands.v1";
const BOOT_KEY = "inventory.brands.demoSeeded.v1";

type BrandApiRecord = {
  id?: number | string;
  /** Some APIs expose numeric DB id separately from public/slug-prefixed `id`. */
  brand_id?: number | string;
  name: string;
  slug: string;
  /** Storage path (relative) — older / alternate API shape */
  logo_path?: string | null;
  /** Full URL or data URL — Laravel resources often use camelCase here */
  logoDataUrl?: string | null;
  logo_data_url?: string | null;
  logo_url?: string | null;
  description?: string | null;
  is_popular?: boolean | number | null;
  isPopular?: boolean | number | null;
  created_at?: string;
  updated_at?: string;
};

/**
 * Normalize list payloads: plain `{ data: [...] }`, `{ brands: [...] }`,
 * Laravel paginator at root, or `{ data: LengthAwarePaginator }` where items live in `data.data`.
 */
function extractBrandRowsFromResponse(body: unknown): BrandApiRecord[] {
  if (Array.isArray(body)) return body;
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;

  const fromBlock = (block: unknown): BrandApiRecord[] | null => {
    if (Array.isArray(block)) return block as BrandApiRecord[];
    if (block && typeof block === "object" && !Array.isArray(block)) {
      const inner = (block as Record<string, unknown>).data;
      if (Array.isArray(inner)) return inner as BrandApiRecord[];
    }
    return null;
  };

  for (const key of ["data", "brands", "items"]) {
    const hit = fromBlock(root[key]);
    if (hit) return hit;
  }
  return [];
}

type UpsertBrandApiResponse = {
  message?: string;
  data?: BrandApiRecord;
};

type DeleteBrandApiResponse = {
  message?: string;
};

type UpsertBrandResult = {
  record: BrandRecord;
  message: string;
};

type DeleteBrandResult = {
  success: boolean;
  message: string;
};

function dedupeById(items: BrandRecord[]): BrandRecord[] {
  const byId = new Map<ID, BrandRecord>();
  for (const item of items) {
    byId.set(item.id, item);
  }
  return Array.from(byId.values());
}

function load(): BrandRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as BrandRecord[];
    return Array.isArray(parsed) ? dedupeById(parsed) : [];
  } catch {
    return [];
  }
}

function save(items: BrandRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

/** Prefer server `isPopular`; fall back to name list for older localStorage rows without the flag. */
const LEGACY_POPULAR_BRAND_NAMES = new Set(
  ["Toyota", "Suzuki", "Honda", "Daihatsu", "Hyundai", "KIA", "Nissan", "Mazda"].map((s) => s.toLowerCase()),
);

export function listBrandRecords(): BrandRecord[] {
  return load()
    .map((b) => ({
      ...b,
      isPopular:
        typeof b.isPopular === "boolean"
          ? b.isPopular
          : LEGACY_POPULAR_BRAND_NAMES.has(String(b.name ?? "").trim().toLowerCase()),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function getBrandById(id: ID): BrandRecord | undefined {
  return load().find((b) => b.id === id);
}

export function keyExists(key: string, exceptId?: ID): boolean {
  const k = slugify(key);
  return load().some((b) => slugify(b.key) === k && b.id !== exceptId);
}

function toLogoUrl(logoPath: string | null | undefined): string | null {
  if (!logoPath) return null;
  if (/^https?:\/\//i.test(logoPath)) return logoPath;
  if (logoPath.startsWith("data:")) return logoPath;
  return logoPath.startsWith("/") ? logoPath : `/${logoPath}`;
}

function resolveBrandLogoFromApi(record: BrandApiRecord): string | null {
  const loose = record as Record<string, unknown>;
  const candidates = [
    record.logoDataUrl,
    record.logo_data_url,
    record.logo_url,
    loose.logoUrl,
  ];
  for (const c of candidates) {
    if (typeof c === "string" && c.trim() !== "") {
      return toLogoUrl(c.trim());
    }
  }
  return toLogoUrl(record.logo_path);
}

function mapApiRecord(record: BrandApiRecord): BrandRecord {
  const id =
    record.brand_id !== undefined && record.brand_id !== null && String(record.brand_id) !== ""
      ? String(record.brand_id)
      : record.id !== undefined && record.id !== null && String(record.id) !== ""
        ? String(record.id)
        : record.slug;
  const loose = record as Record<string, unknown>;
  const popularRaw = record.is_popular ?? record.isPopular ?? loose.is_popular ?? loose.isPopular;
  const isPopular =
    popularRaw === true ||
    popularRaw === 1 ||
    popularRaw === "1" ||
    popularRaw === "true";

  return {
    id,
    name: record.name,
    key: record.slug,
    description: record.description ?? "",
    isPopular,
    logoDataUrl: resolveBrandLogoFromApi(record),
    createdAt: record.created_at || new Date().toISOString(),
    updatedAt: record.updated_at || record.created_at || new Date().toISOString(),
  };
}

export async function syncBrandsFromApi() {
  try {
    /**
     * Laravel paginates brands (default per_page=20, max 100 in BrandService).
     * Without requesting a large page size and following pages, localStorage only
     * held the first API page — client-side grid pagination then had nothing to slice.
     */
    const perPage = 100;
    const allRows: BrandApiRecord[] = [];
    let page = 1;

    while (page < 1000) {
      const response = await api.get("/api/v1/catalog/brands", {
        params: { page, per_page: perPage },
      });
      const body = response.data as Record<string, unknown> | undefined;
      const rows = extractBrandRowsFromResponse(body);
      if (rows.length === 0) break;
      allRows.push(...rows);

      const meta = body?.meta as { total?: number; per_page?: number } | undefined;
      const total = typeof meta?.total === "number" ? meta.total : undefined;
      /** Server may cap or ignore requested per_page (e.g. still 20); use meta for “full page” checks. */
      const serverPerPage = typeof meta?.per_page === "number" ? meta.per_page : perPage;

      if (total !== undefined) {
        if (allRows.length >= total) break;
        // Another page only if this one was full-sized per the API
        if (rows.length < serverPerPage) break;
      } else if (rows.length < serverPerPage) {
        break;
      }

      page += 1;
    }

    const records = allRows.map(mapApiRecord).filter((r) => Boolean(r.id) && r.id !== "__none__");
    save(records);
    return listBrandRecords();
  } catch (error) {
    console.error("[Brands] syncBrandsFromApi failed", error);
    throw error;
  }
}

export async function createBrand(data: {
  name: string;
  key: string;
  description?: string;
  isPopular?: boolean;
  logoFile?: File | null;
}): Promise<UpsertBrandResult> {
  const items = load();
  const form = new FormData();
  form.append("name", data.name.trim());
  form.append("slug", slugify(data.key) || slugify(data.name) || `brand-${Date.now()}`);
  form.append("description", (data.description ?? "").trim());
  form.append("is_popular", data.isPopular ? "1" : "0");
  if (data.logoFile) {
    form.append("logo", data.logoFile);
  }
  const response = await api.post<UpsertBrandApiResponse>("/api/v1/catalog/brands", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  const created = response.data?.data;
  if (!created) {
    throw new Error("Invalid create brand response.");
  }
  const record = mapApiRecord(created);
  const existingIndex = items.findIndex((item) => item.id === record.id);
  if (existingIndex >= 0) {
    items[existingIndex] = record;
  } else {
    items.push(record);
  }
  save(items);
  return {
    record,
    message: response.data?.message || "Brand created successfully.",
  };
}

export async function updateBrand(
  id: ID,
  data: { name: string; key: string; description?: string; isPopular?: boolean; logoFile?: File | null; logoDataUrl?: string | null },
): Promise<UpsertBrandResult | null> {
  const items = load();
  const i = items.findIndex((b) => b.id === id);
  if (i === -1) return null;
  const form = new FormData();
  // Laravel/PHP does not reliably parse multipart bodies on PUT/PATCH.
  // Use method spoofing so the PUT route is matched while keeping POST parsing.
  form.append("_method", "PUT");
  form.append("name", data.name.trim());
  form.append("slug", slugify(data.key) || slugify(data.name) || items[i].key);
  form.append("description", (data.description ?? "").trim());
  form.append("is_popular", data.isPopular ? "1" : "0");
  if (data.logoFile) {
    form.append("logo", data.logoFile);
  }
  if (data.logoFile === null && data.logoDataUrl === null) {
    form.append("remove_logo", "1");
  }

  const response = await api.post<UpsertBrandApiResponse>(`/api/v1/catalog/brands/${id}`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  const updated = response.data?.data;
  if (!updated) {
    throw new Error("Invalid update brand response.");
  }
  items[i] = mapApiRecord(updated);
  save(items);
  return {
    record: items[i],
    message: response.data?.message || "Brand updated successfully.",
  };
}

export async function deleteBrand(id: ID): Promise<DeleteBrandResult> {
  const response = await api.delete<DeleteBrandApiResponse>(`/api/v1/catalog/brands/${id}`);
  const items = load();
  const nextItems = items.filter((b) => b.id !== id);
  const deleted = nextItems.length !== items.length;
  if (deleted) {
    save(nextItems);
  }
  return {
    success: deleted,
    message: response.data?.message || "Brand deleted successfully.",
  };
}

/** Stable demo IDs referenced by `seedDemoProductsIfEmpty`. */
const DEMO_BRANDS: BrandRecord[] = (() => {
  const t = new Date().toISOString();
  return [
    {
      id: "brand_1",
      name: "Toyota",
      key: "toyota",
      description: "Official Toyota marque.",
      isPopular: true,
      logoDataUrl: null,
      createdAt: t,
      updatedAt: t,
    },
    {
      id: "brand_2",
      name: "Honda",
      key: "honda",
      description: "Official Honda marque.",
      isPopular: true,
      logoDataUrl: null,
      createdAt: t,
      updatedAt: t,
    },
    {
      id: "brand_3",
      name: "Suzuki",
      key: "suzuki",
      description: "Official Suzuki marque.",
      isPopular: true,
      logoDataUrl: null,
      createdAt: t,
      updatedAt: t,
    },
    {
      id: "brand_4",
      name: "KIA",
      key: "kia",
      description: "KIA Motors.",
      isPopular: true,
      logoDataUrl: null,
      createdAt: t,
      updatedAt: t,
    },
    {
      id: "brand_5",
      name: "Parts & suppliers",
      key: "parts-suppliers",
      description: "Aftermarket / generic parts brands.",
      isPopular: false,
      logoDataUrl: null,
      createdAt: t,
      updatedAt: t,
    },
  ];
})();

export function seedBrandsIfEmpty() {
  if (typeof localStorage === "undefined") return;
  if (localStorage.getItem(BOOT_KEY)) return;
  if (load().length > 0) {
    localStorage.setItem(BOOT_KEY, "1");
    return;
  }
  save(DEMO_BRANDS);
  localStorage.setItem(BOOT_KEY, "1");
}
